import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { connectRefinementList } from 'react-instantsearch-dom';
import { isEqual } from 'lodash';
import AsyncSelect from 'react-select/async';
import { AttributesInterface, FacetObject } from '../../admin-dashboard-tables/interfaces';
import useFilterFacetValues from '../../../../utils/hooks/useFilterFacetValues';
import { DropdownIndicator } from './react-select-custom-child-components/DropdownHelpers';
import AdminValueContainer from './react-select-custom-child-components/AdminValueContainer';
import CheckBoxOption from './react-select-custom-child-components/checkbox-option';

interface Props {
  searchClient: any,
  items: Array<any>,
  refine: (key: Array<string>) => void,
  attributeObject: AttributesInterface,
  facetFilters: Array<string|Array<string>>,
  setFacetFilters: (filters: Array<string|Array<string>>) => void,
  currentRefinement: Array<string>,
  indexName: string,
  attribute: string,
  max?: number|null,
  andSearch?: boolean,
  addCount?: boolean,
}

const AdminSelectDropdown: React.FC<Props> = ({
  items,
  attributeObject,
  facetFilters,
  setFacetFilters,
  searchClient,
  indexName,
  attribute,
  max,
  andSearch,
  addCount,
}) => {
  const {
    placeholder,
    isSearchable,
    isAsync,
    DisplayComponent,
    isMulti,
  } = attributeObject;
  const [combinedItems, setCombinedItems] = useState(items);
  const [options, setOptions] = useState<Array<FacetObject>>([]);
  const [selectedValue, setSelectedValue] = useState<Array<FacetObject>>([]);

  const filterFacetValues = useFilterFacetValues(attribute, facetFilters);

  const onChangeCallback = useCallback((event) => {
    if (event) {
      // Multiselect will return an array
      if (Array.isArray(event)) {
        if (event.length) {
          const newFacets = [...filterFacetValues()];
          if (andSearch) {
            // console.log('Is andSearch');
            event.forEach((val: { value: string, label: string }) => {
              newFacets.push(`${attribute}:${val.value}`);
            });
          } else {
            // console.log('Is orSearch');
            newFacets.push(
              event.map((val: { value: string, label: string }) => {
                return `${attribute}:${val.value}`;
              }),
            );
          }
          // console.log({ andSearch, newFacets });
          setFacetFilters(newFacets);
        } else {
          setFacetFilters(filterFacetValues());
        }
      } else {
        // @ts-ignore
        const { value } = event;
        if (value) {
          if (value === 'all') {
            setFacetFilters(filterFacetValues());
          } else {
            setFacetFilters([
              ...filterFacetValues(),
              `${attribute}:${value}`,
            ]);
          }
        }
      }
    } else {
      setFacetFilters(filterFacetValues());
    }
  }, [facetFilters, setFacetFilters, andSearch]);

  useEffect(() => {
    if (facetFilters && facetFilters.length > 0) {
      const newValue: Array<FacetObject> = [];
      const found = facetFilters.filter((item: string | Array<string>) => {
        if (Array.isArray(item)) {
          return item.some((facet: string) => facet.indexOf(attribute) !== -1);
        }
        return item.indexOf(attribute) !== -1;
      });

      if (found) {
        // console.log({ andSearch, found });
        if (Array.isArray(found)) {
          const foundArray = found
            .flat()
            .map((facet: string) => {
              // console.log({ facet });
              const value = facet.replace(`${attribute}:`, '');
              return {
                label: value,
                value,
              };
            });
          foundArray.forEach((foundObj: FacetObject) => newValue.push(foundObj));
        }
      }
      // console.log({ newValue });
      if (max && newValue.length > max) {
        const truncatedValues = newValue.slice(0, 2);
        onChangeCallback(truncatedValues);
        if (!isEqual(selectedValue, truncatedValues)) {
          // console.log('setting truncated values: ', truncatedValues);
          setSelectedValue(truncatedValues);
        }
      } else if (!isEqual(selectedValue, newValue)) {
        // console.log('setting selected value: ', newValue);
        setSelectedValue(newValue);
      }
    } else if (selectedValue.length > 0) {
      setSelectedValue([]);
    }
  }, [facetFilters, selectedValue, max, andSearch]);

  const uniqBy = (testItems: Array<any>, property: string) => {
    const seen = {};

    return testItems.filter((item: object) => {
      const val = item[property];

      if (seen[val]) {
        return false;
      }
      seen[val] = true;
      return true;
    });
  };

  const queryAttributesCallback = useCallback((query: string) => {
    // console.log('queryAttributes called');
    return searchClient
      .searchForFacetValues([
        {
          indexName,
          params: {
            facetName: attribute,
            facetQuery: query,
            maxFacetHits: 100,
          },
        },
      ]);
  }, [uniqBy, indexName, attribute, searchClient]);

  const asyncQueryAttributes = useCallback((inputValue: string) => {
    // console.log('async query attrs hit');
    return new Promise<Array<FacetObject>>((resolve) => {
      queryAttributesCallback(inputValue)
        .then(([{ facetHits }]) => {
          resolve(facetHits.map((hit: { value: string, count: number }) => {
            const { value } = hit;
            let label = value;

            if (addCount) {
              const currentFacetStats = items.find((item) => item.label === value);
              label = `${value} (${currentFacetStats ? currentFacetStats.count : 0})`;
            }

            return { value, label };
          }));
        });
    });
  }, [items]);

  // Find facets that return 0 for current search refinement, so that they
  // can still be used in the form
  useEffect(() => {
    // Async dropdowns will run automatically when opened,
    // but we will need this if we're counting occurrences
    if (!isAsync || addCount) {
      queryAttributesCallback('')
        .then(([{ facetHits }]) => {
          const newCombinedItems = uniqBy([
            ...items,
            ...facetHits
              .filter((facet: { value: string }) => {
                return !items.find((item) => item.value === facet.value);
              })
              .map((facet: { value: string, count: number }) => ({
                ...facet,
                label: facet.value,
                value: facet.value,
                isRefined: false,
                count: 0,
              })),
          ], 'label');
          setCombinedItems(newCombinedItems);
        });
    }
  }, [items]);

  const loadOptions = useCallback(() => {
    // console.log('load options called');
    const tempOptions:Array<FacetObject> = [];
    combinedItems.forEach((item) => {
      const label = addCount ? `${item.label} (${item.count})` : item.label;
      const itemObj = {
        value: item.value,
        label,
      };
      tempOptions.push(itemObj);
      // if (item.isRefined) {
      //   selectedValue.push(itemObj);
      // }
      // return true;
    });
    if (!isEqual(tempOptions, options)) {
      setOptions(tempOptions);
    }
  }, [combinedItems, options, setOptions]);

  useEffect(() => {
    if (combinedItems && combinedItems.length > 0) {
      loadOptions();
    }
  }, [combinedItems]);

  const isOptionDisabled = useCallback(() => {
    if (!isMulti) {
      return false;
    }
    if (max) {
      return selectedValue.length >= max;
    }
    return false;
  }, [isMulti, max, selectedValue]);

  useEffect(() => {
    if (max && selectedValue.length > max) {
      const newSelectedValue = selectedValue.slice(0, max);
      // console.log({ newSelectedValue });
      if (!isEqual(selectedValue, newSelectedValue)) {
        // console.log('On change called from effect');
        onChangeCallback(newSelectedValue);
        // setSelectedValue(newSelectedValue);
      }
    }
  }, [max, selectedValue]);

  return (
    <div className="h-100">
      {
        isAsync
          ? (
            <AsyncSelect
              isClearable
              defaultOptions={[
                ...combinedItems.map((item: {label: string, value: string, count: number }) => ({
                  value: item.value,
                  label: addCount ? `${item.label} (${item.count})` : item.label,
                })),
              ]}
              // Disable caching search results if counting since count is dynamic
              cacheOptions={!addCount}
              isMulti={isMulti}
              isOptionDisabled={isOptionDisabled}
              loadOptions={asyncQueryAttributes}
              aria-label={placeholder || ''}
              className="algolia-select btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt align-items-center w-100"
              classNamePrefix="algolia-select"
              value={isMulti ? selectedValue : selectedValue[0]}
              newValueComponent={DisplayComponent}
              components={{
                ValueContainer: AdminValueContainer,
                IndicatorSeparator: () => null,
                DropdownIndicator,
              }}
              onChange={onChangeCallback}
              isSearchable={isSearchable}
              options={options}
              placeholder={placeholder}
            />
          )
          : (
            <Select
              isClearable
              isMulti={isMulti}
              isOptionDisabled={isOptionDisabled}
              aria-label={placeholder}
              className="algolia-select btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt align-items-center w-100"
              classNamePrefix="algolia-select"
              value={isMulti ? selectedValue : selectedValue[0]}
              newValueComponent={DisplayComponent}
              components={{
                ValueContainer: AdminValueContainer,
                Option: CheckBoxOption,
                IndicatorSeparator: () => null,
                DropdownIndicator,
              }}
              onChange={onChangeCallback}
              isSearchable={isSearchable}
              options={options}
              placeholder={placeholder}
            />
          )
      }
      {/* </Dropdown> */}
    </div>
  );
};

export default connectRefinementList(AdminSelectDropdown);
